import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, ApiError } from '@/api/client';
import { authQueryOptions, requireAnonymous } from '@/lib/auth';
import { AuthLayout } from '@/components/AuthLayout';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';

export const Route = createFileRoute('/register')({
  beforeLoad: ({ context }) => requireAnonymous({ context }),
  component: RegisterPage,
});

const registerSchema = z.object({
  firstName: z.string().min(1, 'Required'),
  lastName: z.string().min(1, 'Required'),
  email: z.string().email('Valid email required'),
  phoneNumber: z.string().min(6, 'Required'),
  password: z.string().min(8, 'At least 8 characters'),
});
type RegisterValues = z.infer<typeof registerSchema>;

function RegisterPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [serverError, setServerError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]> | null>(null);

  const form = useForm<RegisterValues>({
    resolver: zodResolver(registerSchema),
    defaultValues: { firstName: '', lastName: '', email: '', phoneNumber: '', password: '' },
  });

  const register = useMutation({
    mutationFn: (values: RegisterValues) =>
      api(`/auth/register`, { method: 'POST', json: values }),
    onSuccess: async () => {
      // Registration signs the user in server-side (sets the auth cookie), but the
      // auth query was cached as `null` by this page's requireAnonymous guard with a
      // 5-minute staleTime. Invalidate it so the /returns guard refetches and sees the
      // new session instead of bouncing to /login. Mirrors the login flow.
      await qc.invalidateQueries({ queryKey: authQueryOptions.queryKey });
      navigate({ to: '/returns' });
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        const problem = err.problem as { title?: string; errors?: Record<string, string[]> } | null;
        setFieldErrors(problem?.errors ?? null);
        setServerError(problem?.title ?? 'Registration failed');
      } else {
        setServerError('Could not reach server');
      }
    },
  });

  const fieldError = (field: string) =>
    fieldErrors ? Object.entries(fieldErrors).find(([k]) => k.toLowerCase().includes(field.toLowerCase()))?.[1]?.[0] : undefined;

  return (
    <AuthLayout
      size="md"
      title="Create your account"
      subtitle="It takes about a minute — no card required."
    >
      <form
        onSubmit={form.handleSubmit((v) => {
          setServerError(null);
          setFieldErrors(null);
          register.mutate(v);
        })}
        className="grid gap-5 sm:grid-cols-2"
      >
        <Field label="First name" error={form.formState.errors.firstName?.message ?? fieldError('firstName')}>
          <Input autoComplete="given-name" {...form.register('firstName')} />
        </Field>
        <Field label="Last name" error={form.formState.errors.lastName?.message ?? fieldError('lastName')}>
          <Input autoComplete="family-name" {...form.register('lastName')} />
        </Field>
        <Field label="Email" error={form.formState.errors.email?.message ?? fieldError('email')} className="sm:col-span-2">
          <Input type="email" autoComplete="email" placeholder="you@example.com" {...form.register('email')} />
        </Field>
        <Field label="Phone" error={form.formState.errors.phoneNumber?.message ?? fieldError('phone')} className="sm:col-span-2">
          <Input type="tel" autoComplete="tel" placeholder="04XX XXX XXX" {...form.register('phoneNumber')} />
        </Field>
        <Field label="Password" error={form.formState.errors.password?.message ?? fieldError('password')} className="sm:col-span-2">
          <Input type="password" autoComplete="new-password" placeholder="At least 8 characters" {...form.register('password')} />
        </Field>

        {serverError && !fieldErrors && (
          <p className="rounded-(--radius) border border-(--color-danger)/25 bg-(--color-danger)/8 px-3.5 py-2.5 text-[13px] font-medium text-(--color-danger) sm:col-span-2">
            {serverError}
          </p>
        )}

        <Button type="submit" size="lg" className="w-full sm:col-span-2" disabled={register.isPending}>
          {register.isPending ? 'Creating account…' : 'Create account'}
        </Button>
      </form>

      <p className="mt-8 text-center text-[14px] text-(--color-ink-muted)">
        Already have an account?{' '}
        <Link to="/login" className="font-semibold text-(--color-navy) hover:underline underline-offset-2">
          Sign in
        </Link>
      </p>
    </AuthLayout>
  );
}

function Field({
  label,
  error,
  children,
  className,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`space-y-1.5 ${className ?? ''}`}>
      <Label>{label}</Label>
      {children}
      {error && <p className="text-[12px] font-medium text-(--color-danger)">{error}</p>}
    </div>
  );
}
